import { useCentralizedMe } from '@crehana/auth';

import { getTodayDate } from '../../utils';
import {
  type TimeAndAttendanceUserAttendanceGetDailyMarkingContextQuery,
  useTimeAndAttendanceUserAttendanceGetDailyMarkingContextQuery as useGetDailyMarkingQuery,
} from './daily-marking.v5.generated';
import { useDailyMarkingMock } from './use-get-daily-marking.mock';

// Derive all widget types from the generated GraphQL query response
// to avoid redefining them manually and keep them in sync with the backend schema.
type TPayload = Extract<
  TimeAndAttendanceUserAttendanceGetDailyMarkingContextQuery['time']['attendance_marks']['get_daily_marking_context'],
  { __typename?: 'TimeGetDailyMarkingContextPayload' }
>;

export type TDailyMarkingContext = TPayload['context'];
export type TShiftContext = NonNullable<TDailyMarkingContext['shift_context']>;
export type TExpectedMark = TDailyMarkingContext['expected_marks'][number];
export type TDaySchedule = NonNullable<TDailyMarkingContext['next_schedule']>;

type TUseGetDailyMarkingReturn = {
  data?: TDailyMarkingContext;
  loading: boolean;
  error: boolean;
  refetch: () => void;
};

export const useGetDailyMarking = ({
  timeZone,
}: {
  timeZone: string;
}): TUseGetDailyMarkingReturn => {
  const mock = useDailyMarkingMock();

  const { currentPageOrganization } = useCentralizedMe();

  const { data, loading, error, refetch } = useGetDailyMarkingQuery({
    variables: {
      input: {
        centralized_organization_id: currentPageOrganization.id,
        date: getTodayDate(),
        timezone: timeZone,
      },
    },
    skip: !!mock,
    context: { clientName: 'v5' },
    fetchPolicy: 'cache-and-network',
  });

  if (mock) {
    return mock;
  }

  const response = data?.time?.attendance_marks?.get_daily_marking_context;
  const context =
    response?.__typename === 'TimeGetDailyMarkingContextPayload'
      ? response.context
      : undefined;

  return {
    data: context,
    loading,
    error:
      !!error ||
      (!!response &&
        response.__typename !== 'TimeGetDailyMarkingContextPayload'),
    refetch: () => {
      refetch();
    },
  };
};
